import { ethers } from "ethers";
import BaseContract from "./BaseContract";
import honeyFarmQueenAbi from "../abis/honeyqueen.json";
import config from "../config";

// Rewards per bee level - index 0,2,4,6,8,10 are unstaked, 1,3,5,7,9 are staked
const REWARDS_PER_BEE_LEVEL = [
    10,  // level 0 unstaked
    14,  // level 1 unstaked
    16,  // level 1 staked
    20,  // level 2 unstaked
    24,  // level 2 staked
    24,  // level 3 unstaked
    30,  // level 3 staked
    31,  // level 4 unstaked
    40,  // level 4 staked
    39,  // level 5 unstaked
    51.3 // level 5 staked
];

interface HoneyFarmQueenContract {
    getBeesState(beesIds: number[]): Promise<BeeState[]>;
    getBeeState(beeId: number): Promise<BeeState>;
    getBeeLevel(beeId: number): Promise<number>;
    claimBeesHoney(beeIds: number[]): Promise<ethers.ContractTransaction>;
    claimBearsHoney(bearIds: number[]): Promise<ethers.ContractTransaction>;
    mintBee(): Promise<ethers.ContractTransaction>;
    mintBeeWithHive?(hiveId: number): Promise<ethers.ContractTransaction>;
    activateBees(beeIds: number[]): Promise<ethers.ContractTransaction>;
    stakeBees(beeIds: number[]): Promise<ethers.ContractTransaction>;
    unstakeBees(beeIds: number[]): Promise<ethers.ContractTransaction>;
    burnBees(beeIds: number[]): Promise<ethers.ContractTransaction>;
    keepBeesActive(beeIds: number[]): Promise<ethers.ContractTransaction>;
    getUnclaimedHoneyForBees(address: string): Promise<number>;
    getUnclaimedHoneyForBee(beeId: number, beeState: BeeState, beeLevel: number): number;
    levelUpBee(
        beeId: number,
        honeyCombs: number[],
        quantities: number[]
    ): Promise<ethers.ContractTransaction>;
}

interface BeeState {
    id: string;
    active: number;
    epochsLeft: number;
    staked: number;
    becameInactiveTime: number;
    lastRewardTime: number;
    lastTimeFed: number;
}

class HoneyFarmQueen extends BaseContract implements HoneyFarmQueenContract {
    constructor(signerOrProvider: ethers.Signer | ethers.providers.Provider) {
        super(config.queenProxyAddress, honeyFarmQueenAbi, signerOrProvider);
    }

    async getUnclaimedHoneyForBees(address: string): Promise<number> {
        const honeyBigNumber = await this.contract.getUnclaimedHoneyForBees(address);
        return Number(ethers.utils.formatEther(honeyBigNumber));
    }

    async getBeesState(beesIds: number[]): Promise<BeeState[]> {
        const result = await this.contract.getBeesState(beesIds);
        return result.map((beeState: any) => ({
            id: beeState.id.toString(),
            active: beeState.active,
            epochsLeft: beeState.epochsLeft,
            staked: beeState.staked,
            becameInactiveTime: beeState.becameInactiveTime.toNumber(),
            lastRewardTime: beeState.lastRewardTime.toNumber(),
            lastTimeFed: beeState.lastTimeFed.toNumber(),
        }));
    }

    async getBeeState(beeId: number): Promise<BeeState> {
        const result = await this.contract.getBeeState(beeId);
        return {
            id: result.id.toString(),
            active: result.active,
            epochsLeft: result.epochsLeft,
            staked: result.staked,
            becameInactiveTime: result.becameInactiveTime.toNumber(),
            lastRewardTime: result.lastRewardTime.toNumber(),
            lastTimeFed: result.lastTimeFed.toNumber(),
        };
    }

    async getBeeLevel(beeId: number): Promise<number> {
        const level = await this.contract.beeLevels(beeId);
        return Number(level.toString());
    }

    /**
     * Calculate unclaimed honey for a single bee based on its state and level
     * @param beeId - The bee ID
     * @param beeState - The bee's current state
     * @param beeLevel - The bee's level (0-5)
     */
    getUnclaimedHoneyForBee(beeId: number, beeState: BeeState, beeLevel: number): number {
        const now = Math.floor(Date.now() / 1000);
        const isStaked = beeState.staked === 1;
        const lastRewardTime = beeState.lastRewardTime;
        
        // Get multiplier index based on level and staked status
        // Level 0: indices 0 (unstaked)
        // Level 1+: unstaked = (level * 2) - 1, staked = level * 2
        let multiplierIndex: number;
        if (beeLevel === 0) {
            multiplierIndex = 0;
        } else {
            multiplierIndex = isStaked ? (beeLevel * 2) : (beeLevel * 2) - 1;
        }
        
        const multiplier = REWARDS_PER_BEE_LEVEL[multiplierIndex] || 10;
        
        if (!isStaked) {
            // Not staked - check if bee is still active
            const cutoff = lastRewardTime + (beeState.epochsLeft * 86400);
            
            if (now >= cutoff) {
                // Bee became inactive, calculate rewards until cutoff
                const activeSeconds = cutoff - lastRewardTime;
                const activeDays = Math.floor(activeSeconds / 86400);
                return 0.13 * (multiplier / 10) * activeDays;
            } else {
                // Bee is still active
                const elapsedSeconds = now - lastRewardTime;
                const elapsedDays = Math.floor(elapsedSeconds / 86400);
                return 0.13 * (multiplier / 10) * elapsedDays;
            }
        } else {
            // Staked bee - no death timer, earns continuously
            const elapsedSeconds = now - lastRewardTime;
            const elapsedDays = Math.floor(elapsedSeconds / 86400);
            return 0.09 * (multiplier / 10) * elapsedDays;
        }
    }

    async claimBeesHoney(beeIds: number[]): Promise<ethers.ContractTransaction> {
        return this.callFunction("claimBeesHoney", [beeIds]);
    }

    async claimBearsHoney(bearIds: number[]): Promise<ethers.ContractTransaction> {
        if (!this.contract.signer) {
            throw new Error("No signer available. Make sure you're connected to a wallet.");
        }
        const signer = await this.contract.signer;
        const contractWithSigner = this.contract.connect(signer);
        
        // Estimate gas and add 20% buffer
        const gasEstimate = await contractWithSigner.estimateGas.claimBearsHoney(bearIds);
        
        return contractWithSigner.claimBearsHoney(bearIds, {
            gasLimit: gasEstimate.mul(120).div(100)
        });
    }

    async mintBee(): Promise<ethers.ContractTransaction> {
        return this.callFunction("mintBee", []);
    }

    async mintBeeWithHive(hiveId: number): Promise<ethers.ContractTransaction> {
        if (!this.contract.signer) {
            throw new Error("No signer available. Make sure you're connected to a wallet.");
        }
        const signer = await this.contract.signer;
        const contractWithSigner = this.contract.connect(signer);
        
        // Estimate gas and add 20% buffer
        const gasEstimate = await contractWithSigner.estimateGas.mintBee(hiveId);
        
        return contractWithSigner.mintBee(hiveId, {
            gasLimit: gasEstimate.mul(120).div(100)
        });
    }

    async activateBees(beeIds: number[]): Promise<ethers.ContractTransaction> {
        return this.callFunction("activateBees", [beeIds]);
    }

    async stakeBees(beeIds: number[]): Promise<ethers.ContractTransaction> {
        return this.callFunction("stakeBees", [beeIds]);
    }

    async unstakeBees(beesIds: number[]): Promise<ethers.ContractTransaction> {
        return this.callFunction("unstakeBees", [beesIds]);
    }

    async burnBees(beesIds: number[]): Promise<ethers.ContractTransaction> {
        return this.callFunction("burnBees", [beesIds]);
    }

    async activateHive(hiveId: number): Promise<ethers.ContractTransaction> {
        return this.callFunction("activateHive", [hiveId]);
    }

    async keepBeesActive(beesIds: number[]): Promise<ethers.ContractTransaction> {
        return this.callFunction("keepBeesActive", [beesIds]);
    }

    async setNoOfEpochsBeforeInactiveBee(epochs: number): Promise<ethers.ContractTransaction> {
        return this.callFunction("setNoOfEpochsBeforeInactiveBee", [epochs]);
    }

    async setInitialStartingPoint(startingPoint: number): Promise<ethers.ContractTransaction> {
        return this.callFunction("setInitialStartingPoint", [startingPoint]);
    }

    async setHiveClaimEpochLength(epochLength: number): Promise<ethers.ContractTransaction> {
        return this.callFunction("setHiveClaimEpochLength", [epochLength]);
    }

    async setHoneyEpochLength(epochLength: number): Promise<ethers.ContractTransaction> {
        return this.callFunction("setHoneyEpochLength", [epochLength]);
    }

    async getCurrentHoneyForBears(owner: string): Promise<number> {
        const honeyBigNumber = await this.contract.getUnclaimedHoneyForBears(owner);
        return parseFloat(ethers.utils.formatUnits(honeyBigNumber, 18));
    }

    async getCurrentHoneyForBees(owner: string): Promise<number> {
        const honeyBigNumber = await this.contract.getUnclaimedHoneyForBees(owner);
        return parseFloat(ethers.utils.formatUnits(honeyBigNumber, 18));
    }

    async levelUpBee(
        beeId: number,
        honeyCombs: number[],
        quantities: number[]
    ): Promise<ethers.ContractTransaction> {
        return this.callFunction("levelUpBee", [beeId, honeyCombs, quantities]);
    }

    async getLastRewardedByBear(bearId: number): Promise<number> {
        const result = await this.contract.getLastRewardedByBear(bearId);
        return result.toNumber();
    }

    async getUnclaimedHoneyForBear(bearId: number): Promise<number> {
        const lastRewardTime = await this.getLastRewardedByBear(bearId);
        const now = Math.floor(Date.now() / 1000); // current timestamp in seconds
        const startingTime = lastRewardTime === 0 ? 1636848000 : lastRewardTime; // use Nov 14 2021 as starting point if never claimed
        const seconds = now - startingTime;
        const days = Math.floor(seconds / 86400); // 86400 seconds in a day
        return days; // one honey per full 24-hour period
    }

    async mintHive(bearId: number): Promise<ethers.ContractTransaction> {
        if (!this.contract.signer) {
            throw new Error("No signer available. Make sure you're connected to a wallet.");
        }
        const signer = await this.contract.signer;
        const contractWithSigner = this.contract.connect(signer);
        
        // Estimate gas and add buffer for network conditions
        const gasEstimate = await contractWithSigner.estimateGas.mintHive(bearId);
        
        return contractWithSigner.mintHive(bearId, {
            gasLimit: gasEstimate.mul(120).div(100) // Add 20% buffer
        });
    }

    /**
     * Migrate a bear from the OpenSea shared storefront to the new Bears Deluxe contract
     * @param osId - The OpenSea shared storefront token ID (string due to large number)
     * @param newId - The new Bears Deluxe token ID (1-6900)
     * @param leaf - The merkle leaf for verification (bytes32)
     * @param merkleProof - The merkle proof array (bytes32[])
     */
    async migrateBear(
        osId: string,
        newId: number,
        leaf: string,
        merkleProof: string[]
    ): Promise<ethers.ContractTransaction> {
        if (!this.contract.signer) {
            throw new Error("No signer available. Make sure you're connected to a wallet.");
        }
        const signer = await this.contract.signer;
        const contractWithSigner = this.contract.connect(signer);
        
        const gasEstimate = await contractWithSigner.estimateGas.migrateBear(
            osId,
            newId,
            leaf,
            merkleProof
        );
        
        return contractWithSigner.migrateBear(
            osId,
            newId,
            leaf,
            merkleProof,
            { gasLimit: gasEstimate.add(100000) }
        );
    }
}


export default HoneyFarmQueen;
